'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import NotificationBell from '@/components/NotificationBell';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Menu, X, Layers, Users, Shield, User, LogIn, PlusCircle, Search, Settings, LayoutDashboard, BookMarked, ScrollText, ChevronDown, LogOut, BarChart2, GraduationCap, MessageSquare, FolderOpen, Inbox, ShieldCheck, Building2, Globe, Mic, Compass, ClipboardList, BookOpen, Archive, Map, Languages } from 'lucide-react';
import { SUPPORTED_LANGUAGES, getTranslations } from '@/lib/i18n';

const navItems = [
  { label: 'The Wall', href: '/the-wall', icon: Layers },
  { label: 'Stories', href: '/contributions', icon: ScrollText },
  { label: 'Discover', href: '/discover', icon: Compass },
  { label: 'Archives', href: '/archives', icon: BookMarked },
  { label: 'Chiefdoms', href: '/chiefdoms', icon: Map },
  { label: 'Community', href: '/community', icon: Users },
  { label: 'Mutauro AI', href: '/mutauro', icon: Languages },
  { label: 'Start a Project', href: '/doc-projects', icon: FolderOpen },
  { label: 'Guide', href: '/contributor-guide', icon: GraduationCap },
];

const userMenuItems = [
  { label: 'My Dashboard', href: '/contributor-dashboard', icon: LayoutDashboard },
  { label: 'My Projects', href: '/my-projects', icon: FolderOpen },
  { label: 'My Stories', href: '/my-stories', icon: ScrollText },
  { label: 'Profile', href: '/contributor-profile', icon: User },
  { label: 'Submit Contribution', href: '/contribution-submission', icon: PlusCircle },
  { label: 'Record a Story', href: '/record-story', icon: Mic },
  { label: 'Messages', href: '/messages', icon: MessageSquare, authOnly: true },
  { label: 'Settings', href: '/settings', icon: Settings },
  { label: 'Request Verification', href: '/verification-request', icon: ShieldCheck, authOnly: true },
  { label: 'Institution Account', href: '/institution-signup', icon: Building2 },
  { label: 'Historian Dashboard', href: '/historian-dashboard', icon: BookOpen, historianOnly: true },
  { label: 'Historian Review Panel', href: '/historian-review', icon: ClipboardList, historianOnly: true },
  { label: 'Archive Manager', href: '/archive-manager', icon: Archive, historianOnly: true },
  { label: 'Curator Queue', href: '/curator-queue', icon: Shield, curatorQueueAccess: true },
  { label: 'Analytics & Monitoring', href: '/analytics', icon: BarChart2, curatorOnly: true },
  { label: 'Contact Submissions', href: '/admin/contact-submissions', icon: Inbox, adminOnly: true },
  { label: 'Verification Requests', href: '/admin/verification-requests', icon: ShieldCheck, adminOnly: true },
  { label: 'Proverbs & Idioms', href: '/admin/proverbs-idioms', icon: BookOpen, adminOnly: true },
  { label: '⚡ Super Admin Panel', href: '/super-admin', icon: Shield, superAdminOnly: true },
];

export default function Topbar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const { user, signOut, getUserProfile } = useAuth();
  const { language, setLanguage } = useLanguage();
  const t = getTranslations(language);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user) { setUserRole(null); return; }
    getUserProfile()
      .then((p: any) => setUserRole(p?.role ?? null))
      .catch(() => setUserRole(null));
  }, [user]);

  const visibleUserMenuItems = userMenuItems.filter(
    (item) => {
      if ((item as any).superAdminOnly) return userRole === 'super_admin';
      if ((item as any).adminOnly) return userRole === 'admin' || userRole === 'super_admin';
      if ((item as any).historianOnly) return userRole === 'historian' || userRole === 'elder' || userRole === 'admin' || userRole === 'super_admin';
      if ((item as any).curatorQueueAccess) return userRole === 'curator' || userRole === 'historian' || userRole === 'elder' || userRole === 'admin' || userRole === 'super_admin';
      if ((item as any).curatorOnly) return userRole === 'curator' || userRole === 'admin' || userRole === 'super_admin';
      if ((item as any).authOnly) return !!user;
      return true;
    }
  );

  const navItemsTranslated = [
    { label: t('nav.theWall'), href: '/the-wall', icon: Layers },
    { label: t('nav.stories'), href: '/contributions', icon: ScrollText },
    { label: 'Discover', href: '/discover', icon: Compass },
    { label: t('nav.archives'), href: '/archives', icon: BookMarked },
    { label: 'Chiefdoms', href: '/chiefdoms', icon: Map },
    { label: t('nav.community'), href: '/community', icon: Users },
    { label: 'Mutauro AI', href: '/mutauro', icon: Languages },
    { label: t('nav.startProject'), href: '/doc-projects', icon: FolderOpen },
    { label: t('nav.guide'), href: '/contributor-guide', icon: GraduationCap },
  ];

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === language) ?? SUPPORTED_LANGUAGES[0];

  // Use static labels on server (and first client render) to avoid hydration mismatch.
  // LanguageContext reads from localStorage in useEffect, so `language` is always 'en' on
  // the server but may differ on the client — causing a mismatch if we call getTranslations
  // during the initial render. We switch to translated labels only after mount.
  const navItemsToRender = mounted ? navItemsTranslated : navItems;

  return (
    <header
      suppressHydrationWarning
      className="fixed top-0 left-0 right-0 z-50 border-b"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(12px)',
        borderColor: 'var(--border)',
      }}
    >
      <div
        suppressHydrationWarning
        className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 h-16 flex items-center justify-between"
      >
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 group">
          <AppLogo size={64} />
        </Link>

        {/* Desktop Nav */}
        <nav suppressHydrationWarning className="hidden md:flex items-center gap-1">
          {navItemsToRender.map((item) => {
            const isActive = pathname === item.href || (item.href === '/' && pathname === '/');
            return (
              <Link
                key={`nav-${item.href}`}
                href={item.href}
                suppressHydrationWarning
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
                  isActive ? 'nav-link-active' : ''
                }`}
                style={{
                  color: isActive ? 'var(--primary)' : 'var(--muted-foreground)',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLAnchorElement).style.color = 'var(--foreground)';
                    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'var(--muted)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLAnchorElement).style.color = 'var(--muted-foreground)';
                    (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                  }
                }}
              >
                <item.icon size={15} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-2">
          <Link
            href="/search"
            className="p-2 rounded-md transition-colors duration-150 flex items-center"
            style={{ color: 'var(--muted-foreground)' }}
            aria-label="Search"
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = 'var(--foreground)';
              (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'var(--muted)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = 'var(--muted-foreground)';
              (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
            }}
          >
            <Search size={18} />
          </Link>

          {/* Language Toggle */}
          <div className="relative" ref={langMenuRef}>
            <button
              onClick={() => setLangMenuOpen(!langMenuOpen)}
              suppressHydrationWarning
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-150 border"
              style={{
                color: 'var(--foreground)',
                borderColor: 'var(--border)',
                backgroundColor: langMenuOpen ? 'var(--muted)' : 'transparent',
              }}
              aria-label="Select language"
              title="Select language"
            >
              <Globe size={14} style={{ color: 'var(--muted-foreground)' }} />
              {mounted && <span>{currentLang.flag}</span>}
              {mounted && <span className="hidden lg:inline">{currentLang.nativeLabel}</span>}
              <ChevronDown size={11} className={`transition-transform duration-150 ${langMenuOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--muted-foreground)' }} />
            </button>
            {langMenuOpen && (
              <div
                className="absolute right-0 mt-2 w-44 rounded-xl border shadow-lg overflow-hidden z-50"
                style={{
                  backgroundColor: 'var(--card)',
                  borderColor: 'var(--border)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)',
                }}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { setLanguage(lang.code); setLangMenuOpen(false); }}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm w-full text-left transition-colors duration-150"
                    style={{
                      color: language === lang.code ? 'var(--primary)' : 'var(--foreground)',
                      backgroundColor: language === lang.code ? '#CC000014' : 'transparent',
                      fontWeight: language === lang.code ? 600 : 400,
                    }}
                    onMouseEnter={(e) => {
                      if (language !== lang.code) {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--muted)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (language !== lang.code) {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span className="text-base">{lang.flag}</span>
                    <div className="flex flex-col">
                      <span className="text-xs font-semibold leading-tight">{lang.nativeLabel}</span>
                      <span className="text-xs leading-tight" style={{ color: 'var(--muted-foreground)' }}>{lang.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Auth area — always renders Sign In on server; swaps to user menu after mount */}
          <div suppressHydrationWarning>
            {!mounted ? (
              <Link
                href="/sign-up-login"
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-150 active:scale-95"
                style={{
                  backgroundColor: 'var(--primary)',
                  color: 'var(--primary-foreground)',
                }}
              >
                <LogIn size={14} />
                Sign In
              </Link>
            ) : user ? (
              <div className="flex items-center gap-2">
                <NotificationBell />
                {/* User dropdown menu */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-150 active:scale-95"
                    style={{
                      backgroundColor: 'var(--primary)',
                      color: 'var(--primary-foreground)',
                    }}
                  >
                    <User size={14} />
                    <span className="max-w-[120px] truncate">
                      {user.email?.split('@')[0] ?? 'Account'}
                    </span>
                    <ChevronDown
                      size={13}
                      className={`transition-transform duration-150 ${userMenuOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {userMenuOpen && (
                    <div
                      className="absolute right-0 mt-2 w-48 rounded-xl border shadow-lg overflow-hidden z-50"
                      style={{
                        backgroundColor: 'var(--card)',
                        borderColor: 'var(--border)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)',
                      }}
                    >
                      {visibleUserMenuItems.map((item) => {
                        const isActive = pathname === item.href;
                        return (
                          <Link
                            key={`user-menu-${item.href}`}
                            href={item.href}
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors duration-150"
                            style={{
                              color: isActive ? 'var(--primary)' : 'var(--foreground)',
                              backgroundColor: isActive ? '#E05A4714' : 'transparent',
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'var(--muted)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = isActive ? '#E05A4714' : 'transparent';
                              }
                            }}
                          >
                            <item.icon size={15} />
                            {item.label}
                          </Link>
                        );
                      })}
                      <div className="border-t" style={{ borderColor: 'var(--border)' }}>
                        <button
                          onClick={() => { setUserMenuOpen(false); signOut?.(); }}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm w-full text-left transition-colors duration-150"
                          style={{ color: 'var(--muted-foreground)' }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.color = 'var(--foreground)';
                            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--muted)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted-foreground)';
                            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                          }}
                        >
                          <LogOut size={15} />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Link
                href="/sign-up-login"
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-150 active:scale-95"
                style={{
                  backgroundColor: 'var(--primary)',
                  color: 'var(--primary-foreground)',
                }}
              >
                <LogIn size={14} />
                Sign In
              </Link>
            )}
          </div>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 rounded-md"
          style={{ color: 'var(--muted-foreground)' }}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          suppressHydrationWarning
          className="md:hidden border-t px-4 py-3 space-y-1 max-h-[80vh] overflow-y-auto"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
        >
          {navItemsToRender.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={`mobile-nav-${item.href}`}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150"
                style={{
                  color: isActive ? 'var(--primary)' : 'var(--foreground)',
                  backgroundColor: isActive ? '#E05A4714' : 'transparent',
                }}
              >
                <item.icon size={16} />
                {item.label}
              </Link>
            );
          })}

          {/* Language selector in mobile */}
          <div className="pt-1 pb-0.5 px-3">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
              Language / Mutauro / Ulimi
            </span>
          </div>
          <div className="flex flex-wrap gap-2 px-3 pb-1">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => { setLanguage(lang.code); setMobileOpen(false); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150"
                style={{
                  backgroundColor: language === lang.code ? '#E05A4714' : 'var(--muted)',
                  borderColor: language === lang.code ? 'var(--primary)' : 'var(--border)',
                  color: language === lang.code ? 'var(--primary)' : 'var(--foreground)',
                  fontWeight: language === lang.code ? 600 : 400,
                }}
              >
                <span>{lang.flag}</span>
                <span>{lang.nativeLabel}</span>
              </button>
            ))}
          </div>

          {/* Mobile auth area — suppressHydrationWarning to avoid SSR mismatch */}
          <div suppressHydrationWarning>
            {mounted && user && (
              <>
                <div className="pt-1 pb-0.5 px-3">
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
                    My Account
                  </span>
                </div>
                {visibleUserMenuItems.map((item) => {
                  const isActive = pathname === item.href;
                  return (
                    <Link
                      key={`mobile-user-${item.href}`}
                      href={item.href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150"
                      style={{
                        color: isActive ? 'var(--primary)' : 'var(--foreground)',
                        backgroundColor: isActive ? '#E05A4714' : 'transparent',
                      }}
                    >
                      <item.icon size={16} />
                      {item.label}
                    </Link>
                  );
                })}
              </>
            )}

            <div className="pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
              {mounted && user && (
                <div className="flex justify-center mb-2">
                  <NotificationBell />
                </div>
              )}
              {mounted && user ? (
                <button
                  onClick={() => { setMobileOpen(false); signOut?.(); }}
                  className="flex items-center justify-center gap-1.5 w-full px-4 py-2 rounded-md text-sm font-medium"
                  style={{ backgroundColor: 'var(--muted)', color: 'var(--foreground)' }}
                >
                  <LogOut size={14} />
                  Sign Out
                </button>
              ) : (
                <Link
                  href="/sign-up-login"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-1.5 w-full px-4 py-2 rounded-md text-sm font-medium"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  <LogIn size={14} />
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}